import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getOrderItems, subscribeToOrder, updateItemPickStatus, confirmOrderPick, deleteOrder } from '../supabase/services';
import { fetchReadyToPickOrders, fetchShipTo } from '../supabase/orderService';
import BarcodeScanner from './BarcodeScanner';
import Toast from './Toast';
import Modal from './common/Modal';
import LastScanPanel from './common/LastScanPanel';
import DueDate from './common/DueDate';
import Meter from './common/Meter';
import useDeviceDetect from '../hooks/useDeviceDetect';
import useBarcodeBuffer from '../hooks/useBarcodeBuffer';
import useAnyModalOpen from '../hooks/useAnyModalOpen';
import useToast from '../hooks/useToast';
import scanFeedback from '../utils/scanFeedback';
import { formatDuration } from '../utils/format';
import {
  isInsufficientStock,
  summarizePick,
  formatPickSummary,
  orderItemsForDisplay,
  resolveScan,
  findNextOrder,
  pickNotes,
} from './orders/pickLogic';
import { splitManufactureItems, buildProductionSheetHtml } from './orders/productionSheet';
import { countLabelBoxes, addressLines, buildBoxLabelsHtml, MAX_LABEL_BOXES } from './orders/boxLabels';
import {
  ArrowLeft, ArrowRight, Printer, CheckCircle, CheckCircle2, Circle, Trash2, Power, PowerOff, Tag,
} from 'lucide-react';
import './OrderDetail.css';

// How long a scanned row stays highlighted, and how long a just-completed line keeps
// its place before sinking to the picked group (kept equal so it never moves mid-highlight).
const HIGHLIGHT_MS = 1500;

let scanSeq = 0;

// Props:
//   orderId        order to show (from the URL)
//   initialOrder   optional row from the list, shown until the live copy loads
//   onBack(flash?) back to the list; flash = { message, type } to toast there
//   onOpenOrder(order)  open another order (Next order)
const OrderDetail = ({ orderId, initialOrder, onBack, onOpenOrder }) => {
  // Live copy of the order row (status, pick times) kept in sync via realtime
  const [order, setOrder] = useState(initialOrder || null);
  const [orderMissing, setOrderMissing] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanActive, setScanActive] = useState(true);
  // Result of the most recent scan; stays up until the next one
  const [lastScan, setLastScan] = useState(null);
  // { id, seq }: seq alternates the animation so re-scanning the same row replays it
  const [highlight, setHighlight] = useState(null);
  // Lines just completed by a scan; they keep their place for HIGHLIGHT_MS
  const [holdIds, setHoldIds] = useState(() => new Set());
  const timersRef = useRef(new Set());
  const isProcessingScanRef = useRef(false);
  const scanQueueRef = useRef([]);
  const processScanRef = useRef(null);
  // Item ids this client has fully picked but whose realtime refetch hasn't landed yet.
  // Consecutive fast scans of a duplicated barcode would otherwise see the stale `items`
  // and hit the same (already picked) line instead of the next unpicked one.
  const locallyPickedRef = useRef(new Set());

  const { showToast, toastProps } = useToast();
  const anyModalOpen = useAnyModalOpen();

  const [showPrintModal, setShowPrintModal] = useState(false);
  // Box labels: { loading, error, address, reference } once the dialog is opened
  const [labelDialog, setLabelDialog] = useState(null);
  const [labelBoxes, setLabelBoxes] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const [confirming, setConfirming] = useState(false);
  const [justConfirmed, setJustConfirmed] = useState(false);
  const [findingNext, setFindingNext] = useState(false);

  // Touch devices get a sticky scan-status strip at the top instead of the scanner box
  const { isMobile, isTablet, isIPad } = useDeviceDetect();
  const isTouchLayout = isMobile || isTablet || isIPad;

  // Run fn after ms; cleared on unmount
  const later = useCallback((fn, ms) => {
    const timer = setTimeout(() => {
      timersRef.current.delete(timer);
      fn();
    }, ms);
    timersRef.current.add(timer);
  }, []);

  useEffect(() => {
    const timers = timersRef.current;
    // Subscribe to real-time updates on order items
    const unsubscribe = getOrderItems(orderId, (itemsList, error) => {
      setLoading(false);
      if (error) {
        showToast('Could not load order items. Please go back and retry.', 'error');
        return;
      }
      // Realtime has confirmed these picks; stop tracking them locally.
      itemsList.forEach(item => {
        if (item.picked) locallyPickedRef.current.delete(item.id);
      });
      setItems(itemsList);
    });

    // Keep order header fields (status, picking time) live
    const unsubscribeOrder = subscribeToOrder(orderId, (liveOrder, error) => {
      if (error) return;
      if (liveOrder) {
        setOrder(liveOrder);
        setOrderMissing(false);
      } else {
        setOrderMissing(true);
      }
    });

    return () => {
      unsubscribe();
      unsubscribeOrder();
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, [orderId, showToast]);

  // Persist the scan result and fire sound/vibration/flash. Every call gets a fresh
  // timestamp so repeating the same bad scan replays the feedback.
  const reportScan = useCallback((kind, panel) => {
    scanFeedback(kind);
    scanSeq += 1;
    setLastScan({ ...panel, at: `${Date.now()}-${scanSeq}` });
  }, []);

  const highlightRow = useCallback((itemId) => {
    scanSeq += 1;
    const seq = scanSeq;
    setHighlight({ id: itemId, seq });
    later(() => setHighlight((h) => (h && h.seq === seq ? null : h)), HIGHLIGHT_MS);
  }, [later]);

  const holdRow = useCallback((itemId) => {
    setHoldIds((ids) => new Set(ids).add(itemId));
    later(() => setHoldIds((ids) => {
      const next = new Set(ids);
      next.delete(itemId);
      return next;
    }), HIGHLIGHT_MS);
  }, [later]);

  // Process a single barcode scan. Serialization is handled by handleScan below.
  const processScan = useCallback(async (barcode) => {
    // Save the current scroll position before processing
    const scrollPosition = window.scrollY;
    const restoreScroll = () => setTimeout(() => window.scrollTo(0, scrollPosition), 0);

    const result = resolveScan(items, barcode, locallyPickedRef.current);

    if (result.kind === 'notFound') {
      reportScan('error', {
        status: 'error',
        title: 'Not on this order',
        detail: barcode,
        meta: 'Check the item and scan again',
      });
      restoreScroll();
      return;
    }

    const { item } = result;

    if (result.kind === 'alreadyPicked') {
      highlightRow(item.id);
      reportScan('duplicate', {
        status: 'warning',
        title: 'Already picked',
        detail: item.name,
        meta: `All ${item.quantity} picked — don't add another`,
      });
      restoreScroll();
      return;
    }

    highlightRow(item.id);

    try {
      const update = await updateItemPickStatus(orderId, item.id, barcode);
      if (update.isFullyPicked) {
        locallyPickedRef.current.add(item.id);
        holdRow(item.id);
        reportScan('success', {
          status: 'success',
          title: `Line complete · ${update.scanCount} of ${update.requiredQuantity}`,
          detail: item.name,
          meta: barcode,
        });
      } else {
        reportScan('success', {
          status: 'success',
          title: `Picked ${update.scanCount} of ${update.requiredQuantity}`,
          detail: item.name,
          meta: `${update.requiredQuantity - update.scanCount} more to scan`,
        });
      }
    } catch (error) {
      // Picked from another device between our read and the update: an over-pick
      if (error.message === 'Item already picked') {
        reportScan('duplicate', {
          status: 'warning',
          title: 'Already picked',
          detail: item.name,
          meta: `All ${item.quantity} picked — don't add another`,
        });
      } else {
        reportScan('error', {
          status: 'error',
          title: 'Scan not saved',
          detail: item.name,
          meta: error.message,
        });
      }
    }
    restoreScroll();
  }, [items, orderId, reportScan, highlightRow, holdRow]);

  // Keep a ref to the latest processScan so the queue drain always uses fresh `items`.
  processScanRef.current = processScan;

  // Public scan entry point. Serializes scans through a queue so that rapid scans
  // (e.g. scanning a multi-quantity line several times quickly) are never dropped —
  // each is processed in order against the atomic server-side update.
  const handleScan = useCallback(async (barcode) => {
    scanQueueRef.current.push(barcode);
    if (isProcessingScanRef.current) return;
    isProcessingScanRef.current = true;
    try {
      while (scanQueueRef.current.length > 0) {
        const next = scanQueueRef.current.shift();
        await processScanRef.current(next);
      }
    } finally {
      isProcessingScanRef.current = false;
    }
  }, []);

  const handleScanWhileOff = useCallback(() => {
    scanFeedback('error');
    showToast('Scanner is off — turn it on to scan', 'warning');
  }, [showToast]);

  const handleScanWhileDialogOpen = useCallback(() => {
    scanFeedback('error');
    showToast('Close the dialog before scanning', 'warning');
  }, [showToast]);

  // Global keyboard listener for hardware barcode scanners; paused while a dialog is up.
  // A disabled handler is always passed so the listener stays attached and swallows the
  // scanner's trailing Enter - otherwise it would click the dialog's focused button.
  useBarcodeBuffer(handleScan, {
    active: scanActive && !anyModalOpen,
    onKeyWhileDisabled: anyModalOpen ? handleScanWhileDialogOpen : handleScanWhileOff,
  });

  const summary = summarizePick(items);
  const unitsLeft = summary.unitsTotal - summary.unitsPicked;
  const notes = pickNotes(order?.notes);
  const isConfirmed = order?.status === 'Picked Complete' || justConfirmed;
  const awaitingConfirmation = !loading && summary.linesTotal > 0
    && summary.linesPicked === summary.linesTotal && !isConfirmed;

  // Every line is picked; the operator confirms before the order moves to Completed Orders
  const handleConfirmPick = async () => {
    if (confirming) return;
    setConfirming(true);
    try {
      await confirmOrderPick(orderId);
      setJustConfirmed(true);
      showToast(`Order ${orderId} confirmed`, 'success');
    } catch (error) {
      showToast(error.message || 'Could not confirm the pick. Please try again.', 'error');
    } finally {
      setConfirming(false);
    }
  };

  const handleNextOrder = async () => {
    if (findingNext) return;
    setFindingNext(true);
    try {
      const next = findNextOrder(await fetchReadyToPickOrders(), orderId);
      if (next) {
        onOpenOrder(next);
      } else {
        onBack({ message: 'All caught up — no orders to pick', type: 'success' });
      }
    } catch (error) {
      showToast('Could not load the next order. Check your connection and retry.', 'error');
      setFindingNext(false);
    }
  };

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await deleteOrder(orderId);
      onBack({ message: `Order ${orderId} deleted`, type: 'success' });
    } catch (error) {
      setDeleteError(`Could not delete the order: ${error.message}`);
      setDeleteLoading(false);
    }
  };

  const displayItems = orderItemsForDisplay(items, holdIds);

  // Show print options modal
  const handlePrintProductionSheet = () => {
    if (splitManufactureItems(items).all.length === 0) {
      showToast('No items need manufacturing - all items have sufficient stock', 'warning');
      return;
    }
    setShowPrintModal(true);
  };

  // Execute print with selected type
  const executePrint = (printType) => {
    setShowPrintModal(false);

    const { frames, mounts } = splitManufactureItems(items);
    if (printType === 'frames' && frames.length === 0) {
      showToast('No frames need manufacturing', 'warning');
      return;
    }
    if (printType === 'mounts' && mounts.length === 0) {
      showToast('No mounts need manufacturing', 'warning');
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      showToast('Please allow pop-ups to print the production sheet.', 'warning');
      return;
    }
    printWindow.document.write(buildProductionSheetHtml(order || { id: orderId }, items, printType));
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  // Box labels: the address is read live from Priority each time the dialog opens
  const openLabelDialog = async () => {
    setLabelBoxes(String(countLabelBoxes(items) || 1));
    setLabelDialog({ loading: true });
    try {
      const { address, reference } = await fetchShipTo(orderId);
      setLabelDialog({ loading: false, address, reference });
    } catch (error) {
      setLabelDialog({ loading: false, error: error.message || 'Could not load the delivery address' });
    }
  };

  const labelBoxCount = Math.floor(Number(labelBoxes));
  const labelBoxesValid = labelBoxCount >= 1 && labelBoxCount <= MAX_LABEL_BOXES;

  const printBoxLabels = () => {
    if (!labelDialog?.address || !labelBoxesValid) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      showToast('Please allow pop-ups to print the labels.', 'warning');
      return;
    }
    printWindow.document.write(buildBoxLabelsHtml({
      orderId,
      address: labelDialog.address,
      reference: labelDialog.reference,
      boxes: labelBoxCount,
    }));
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
    setLabelDialog(null);
  };

  const backButton = (
    <button type="button" className="back-button" onClick={() => onBack()} aria-label="Back to Orders">
      <ArrowLeft size={18} aria-hidden="true" /> Orders
    </button>
  );

  if (orderMissing) {
    return (
      <div className="order-detail-container">
        <div className="header-top-row">{backButton}</div>
        <div className="order-missing" role="alert">
          <h2>Order #{orderId} not found</h2>
          <p>It may have been deleted, or the link is wrong.</p>
        </div>
      </div>
    );
  }

  const lastScanPanel = (compact) => (
    <LastScanPanel
      status={lastScan?.status || 'idle'}
      title={lastScan?.title || (scanActive ? 'Ready — scan an item' : 'Scanner is off')}
      detail={lastScan?.detail}
      meta={lastScan?.meta}
      timestamp={lastScan?.at}
      compact={compact}
    />
  );

  const scannerSwitch = (
    <button
      type="button"
      role="switch"
      aria-checked={scanActive}
      onClick={() => setScanActive(!scanActive)}
      className={`strip-scanner-toggle ${scanActive ? 'active' : 'inactive'}`}
    >
      {scanActive ? <Power size={20} aria-hidden="true" /> : <PowerOff size={20} aria-hidden="true" />}
      <span>Scanner</span>
      <span className="strip-scanner-state" aria-hidden="true">{scanActive ? 'ON' : 'OFF'}</span>
    </button>
  );

  return (
    <div className="order-detail-container">
      <Toast {...toastProps} />

      <Modal
        open={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        title="Print production sheet"
        titleIcon={<Printer size={22} />}
        size="sm"
        footer={
          <button type="button" className="modal-secondary-btn" onClick={() => setShowPrintModal(false)}>
            Cancel
          </button>
        }
      >
        <p className="print-modal-intro">Which lines short on stock should go on the sheet?</p>
        <div className="print-modal-buttons">
          <button type="button" className="print-option-btn" onClick={() => executePrint('frames')}>
            Frames only
          </button>
          <button type="button" className="print-option-btn" onClick={() => executePrint('mounts')}>
            Mounts only
          </button>
          <button type="button" className="print-option-btn" onClick={() => executePrint('both')}>
            Frames and mounts
          </button>
        </div>
      </Modal>

      <Modal
        open={Boolean(labelDialog)}
        onClose={() => setLabelDialog(null)}
        title="Print box labels"
        titleIcon={<Tag size={22} />}
        size="sm"
        footer={
          <>
            <button type="button" className="modal-secondary-btn" onClick={() => setLabelDialog(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="print-option-btn"
              onClick={printBoxLabels}
              disabled={!labelDialog?.address || !labelBoxesValid}
            >
              Print {labelBoxesValid ? labelBoxCount : ''} label{labelBoxCount === 1 ? '' : 's'}
            </button>
          </>
        }
      >
        {labelDialog?.loading && <p className="print-modal-intro">Loading the delivery address from Priority…</p>}
        {labelDialog?.error && <div className="delete-error" role="alert">{labelDialog.error}</div>}
        {labelDialog?.address && (
          <>
            <div className="label-address-preview">
              {labelDialog.address.name && <strong>{labelDialog.address.name}</strong>}
              {addressLines(labelDialog.address).map((line, i) => <span key={i}>{line}</span>)}
              {labelDialog.address.postcode && <strong>{labelDialog.address.postcode}</strong>}
              {labelDialog.reference && <span className="label-ref">Ref {labelDialog.reference}</span>}
            </div>
            <label className="label-boxes-field">
              Number of boxes
              <input
                type="number"
                min="1"
                max={MAX_LABEL_BOXES}
                inputMode="numeric"
                value={labelBoxes}
                onChange={(e) => setLabelBoxes(e.target.value)}
              />
            </label>
            <p className="label-hint">
              One label per unit, delivery line excluded. Change it if frames share a box.
              Pick the Zebra (6x4in labels) in the print dialog.
            </p>
          </>
        )}
      </Modal>

      <Modal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete order?"
        titleIcon={<Trash2 size={22} />}
        tone="danger"
        size="sm"
        dismissible={!deleteLoading}
        footer={
          <>
            <button
              type="button"
              className="modal-secondary-btn"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleteLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="modal-danger-btn"
              onClick={handleConfirmDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? 'Deleting…' : 'Delete order'}
            </button>
          </>
        }
      >
        <p className="delete-modal-text">
          Delete order <strong>{orderId}</strong> and all its pick progress? This cannot be undone.
        </p>
        {deleteError && <div className="delete-error" role="alert">{deleteError}</div>}
      </Modal>

      {/* Touch devices: scanner state and the last scan stay in view while scrolling the list */}
      {isTouchLayout && (
        <div className={`scan-status-strip ${scanActive ? '' : 'is-off'}`}>
          {scannerSwitch}
          <div className="scan-status-strip-panel">{lastScanPanel(true)}</div>
        </div>
      )}

      {/* Order header: one row - back, order, customer, dispatch, print actions */}
      <div className="order-detail-header">
        <div className="header-top-row">
          <div className="order-title-group">
            {backButton}
            <h2>Order #{orderId}</h2>
            {order ? (
              <div className="customer-info">
                <p><strong>Customer:</strong> {order.customerName || 'Unknown'}</p>
                <p className="dispatch-date-line"><strong>Dispatch:</strong>
                  <DueDate
                    date={order.dispatchDate || order.createTime}
                    showRelative={order.status !== 'Picked Complete'}
                    inline
                  />
                </p>
                {order.status === 'Picked Complete' && (
                  <p><strong>Picking time:</strong> {formatDuration(order.pickStartTime, order.pickCompleteTime)}</p>
                )}
              </div>
            ) : (
              <div className="skeleton-text skeleton customer-info-skeleton" aria-label="Loading order" />
            )}
          </div>
          <div className="header-print-actions">
            <button type="button" className="print-production-btn" onClick={openLabelDialog} disabled={loading}>
              <Tag size={14} aria-hidden="true" /> Box labels
            </button>
            <button type="button" className="print-production-btn" onClick={handlePrintProductionSheet} disabled={loading}>
              <Printer size={14} aria-hidden="true" /> Production sheet
            </button>
          </div>
        </div>
        {notes && (
          <p className="order-notes" title={notes}><strong>Notes:</strong> {notes}</p>
        )}
      </div>

      {/* Scanner (desktop) + what's left to pick, one strip - same pattern as Work Orders */}
      <div className="pick-overview">
        {!isTouchLayout && (
          <div className="pick-scanner">
            <BarcodeScanner
              onScan={handleScan}
              active={scanActive}
              setActive={setScanActive}
              title={null}
            />
          </div>
        )}
        <div className="pick-summary">
          <p className="summary-remaining">
            <span className="summary-remaining-value">{loading ? '–' : unitsLeft.toLocaleString()}</span>
            <span className="summary-remaining-label"> units left of {summary.unitsTotal.toLocaleString()}</span>
          </p>
          <Meter value={summary.unitsPicked} max={summary.unitsTotal} label="Units picked" />
          <p className="summary-detail" title={formatPickSummary(summary)}>
            {loading ? 'Loading lines…' : `${summary.linesPicked} of ${summary.linesTotal} lines picked`}
          </p>
        </div>
      </div>

      {/* Desktop: result of the last scan, one line above the list */}
      {!isTouchLayout && lastScanPanel(true)}

      {awaitingConfirmation && (
        <div className="confirm-pick-panel">
          <p>All lines are picked. Confirm the pick to move this order to Completed Orders.</p>
          <button
            type="button"
            className="confirm-pick-btn"
            onClick={handleConfirmPick}
            disabled={confirming}
          >
            <CheckCircle size={20} aria-hidden="true" /> {confirming ? 'Confirming…' : 'Confirm Pick'}
          </button>
        </div>
      )}

      {isConfirmed && (
        <div className="confirmed-panel" role="status">
          <p><CheckCircle2 size={20} aria-hidden="true" /> Pick confirmed — this order is in Completed Orders.</p>
          <div className="confirmed-actions">
            <button type="button" className="secondary-action-btn" onClick={() => onBack()}>
              Back to orders
            </button>
            <button type="button" className="next-order-btn" onClick={handleNextOrder} disabled={findingNext}>
              {findingNext ? 'Finding next…' : 'Next order'} <ArrowRight size={20} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {/* Items to Pick */}
      <div className="items-list">
        <h3 className="sr-only">Lines to pick</h3>
        {loading ? (
          <div className="skeleton-table">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="skeleton-table-row">
                <div className="skeleton-table-cell skeleton" style={{ width: '60px' }} />
                <div className="skeleton-table-cell skeleton" style={{ flex: 3 }} />
                <div className="skeleton-table-cell skeleton" style={{ flex: 2 }} />
                <div className="skeleton-table-cell skeleton" style={{ width: '50px' }} />
                <div className="skeleton-table-cell skeleton" style={{ flex: 2 }} />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <p className="no-items">This order has no lines.</p>
        ) : (
          <table className="items-table">
            <thead>
              <tr>
                <th className="status-column">Status</th>
                <th>Item</th>
                <th className="barcode-column">Barcode</th>
                <th className="quantity-column">Qty</th>
                <th className="scan-progress-column">Scanned</th>
                <th className="warehouse-balance-column">Stock</th>
              </tr>
            </thead>
            <tbody>
              {displayItems.map(item => {
                const short = !item.picked && isInsufficientStock(item);
                const highlighted = highlight?.id === item.id;
                return (
                  <tr
                    key={item.id}
                    className={[
                      'item-row',
                      item.picked ? 'picked' : 'pending',
                      short ? 'out-of-stock' : '',
                      highlighted ? `highlighted-scan highlight-${highlight.seq % 2}` : '',
                    ].join(' ')}
                  >
                    <td className="status-column">
                      <span className={`status-indicator ${item.picked ? 'picked' : 'pending'}`}>
                        {item.picked
                          ? <CheckCircle2 size={16} aria-hidden="true" />
                          : <Circle size={16} aria-hidden="true" />}
                        {item.picked ? 'Picked' : 'To pick'}
                      </span>
                    </td>
                    <td className="item-name-cell">{item.name}</td>
                    <td className="barcode-cell barcode-column">{item.barcode}</td>
                    <td className="quantity-cell">{item.quantity}</td>
                    <td className="scan-progress-column">
                      {item.scanCount > 0 && !item.picked ? (
                        <div className="scan-progress">
                          <span>{item.scanCount} of {item.quantity} scanned</span>
                          <div className="scan-progress-bar">
                            <div
                              className="scan-progress-fill"
                              style={{ width: `${item.quantity > 0 ? Math.min((item.scanCount / item.quantity) * 100, 100) : 0}%` }}
                            ></div>
                          </div>
                        </div>
                      ) : (
                        item.picked ? 'Complete' : 'Not started'
                      )}
                    </td>
                    <td className={`warehouse-balance-column ${short ? 'insufficient-stock' : ''}`}>
                      <span className="balance-value">{item.warehouseBalance ?? 'N/A'}</span>
                      {short && (
                        <span className="short-by">
                          Short by {Number(item.quantity) - Number(item.warehouseBalance)}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Touch: manual barcode entry below the list (the on/off switch is in the strip) */}
      {isTouchLayout && (
        <div className="scanner-container">
          <BarcodeScanner
            onScan={handleScan}
            active={scanActive}
            setActive={setScanActive}
            showToggle={false}
            title="Type a barcode"
            label="Barcode won't scan? Type it here"
          />
        </div>
      )}

      <div className="order-danger-zone">
        <button
          type="button"
          className="delete-order-btn"
          onClick={() => { setDeleteError(null); setShowDeleteModal(true); }}
        >
          <Trash2 size={18} aria-hidden="true" /> Delete order
        </button>
      </div>
    </div>
  );
};

export default OrderDetail;
